import { ACCESS_PACKAGE } from '@choco-admin/types';
import { Chip, Input, Switch, Tooltip } from '@heroui/react';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FiAlertTriangle } from 'react-icons/fi';

import { AlertCard } from '../../../../../../components/AlertCard';
import {
  ChocoSelect,
  LocaleSelectField,
} from '../../../../../../components/FormComponents';
import {
  isIntegrationPayloadLocked,
  isTestDistributor,
  useAccessPackageAccess,
  useIntegrationSettingsAccess,
} from '../../../../../../featureFlags';
import {
  AccessPackageSection,
  IntegrationSettingsSection,
  LockedFieldTooltip,
} from '../../components';
import { useSupplierForm } from '../../SupplierFormProvider';
import { type SupplierFormData, type TabProps } from '../../types';

import { TimezoneField } from './components';

interface SettingsTabProps extends TabProps {}

/**
 * One typography scale for every row on this tab.
 *
 * These are `ChocoSelect`'s own label and description classes. The rows that
 * hand-roll a label borrow them rather than picking their own, because a tab
 * where one field's label is a size smaller than its neighbour's reads as
 * broken even when every row is individually fine.
 */
const ROW_LABEL_CLASS = 'text-sm font-medium text-foreground';
const ROW_DESCRIPTION_CLASS = 'text-xs text-default-600';

/**
 * Matches the shared `Input` component, which this tab cannot use for the
 * decimal places field: that component takes its label as a string, and the
 * warning beside this one is an interactive control.
 */
const INPUT_CLASS_NAMES = {
  input: 'text-foreground bg-transparent px-3 py-2',
  // The width caps the box, not the field: a one-digit answer gets a short box,
  // while the error message underneath still has the whole column to wrap in.
  inputWrapper:
    'max-w-[13rem] bg-default-100 border-default-300 hover:border-default-400 focus-within:border-primary shadow-sm hover:shadow-md transition-all duration-200',
};

interface ToggleRowProps {
  label: string;
  description: string;
  isSelected: boolean;
  onChange: (value: boolean) => void;
  testId?: string;
  /** Read-only with no explanation here, because something beside it carries one. */
  isDisabled?: boolean;
  /**
   * Why the row is read-only, shown in place of the description, and disabling on
   * its own. For a rule with nowhere else to state it: the Integrated toggle has a
   * tooltip and a callout, so it uses `isDisabled`.
   */
  lockedReason?: string;
}

/**
 * One switch and its explanation, on a single row.
 *
 * Every toggle on this tab has the same shape, so the reader scans a column of
 * rows instead of a stack of competing headings. A row label is not a heading:
 * section titles are the only `h3` here.
 */
const ToggleRow: React.FC<ToggleRowProps> = ({
  label,
  description,
  isSelected,
  onChange,
  testId,
  isDisabled = false,
  lockedReason,
}) => (
  <div className="flex items-start justify-between gap-6">
    <div>
      <p className={ROW_LABEL_CLASS}>{label}</p>
      {/* The reason replaces the description rather than joining it. Someone who
          cannot change the row needs to know why before they need to know what it
          does. */}
      <p className={`mt-1 ${ROW_DESCRIPTION_CLASS}`}>
        {lockedReason ?? description}
      </p>
    </div>
    <Switch
      isSelected={isSelected}
      onValueChange={onChange}
      isDisabled={isDisabled || !!lockedReason}
      size="lg"
      className="flex-shrink-0"
      {...(testId && { 'data-testid': testId })}
      aria-label={label}
    />
  </div>
);

/** A section title, with an optional line explaining the group beneath it. */
const SectionHeading: React.FC<{ title: string; description?: string }> = ({
  title,
  description,
}) => (
  <div className="space-y-1">
    <h3 className="text-medium font-semibold text-foreground">{title}</h3>
    {description && <p className={ROW_DESCRIPTION_CLASS}>{description}</p>}
  </div>
);

export const SettingsTab: React.FC<SettingsTabProps> = () => {
  // `accessPackage` is read from the loaded distributor rather than the form: it
  // is not a form field, because `supplierUpdate` cannot write it.
  const { mode, supplier } = useSupplierForm();
  const { canView: canViewAccessPackage } = useAccessPackageAccess();

  /**
   * Customer acquisition is Premium-only, the rule the legacy admin enforced with
   * the same tooltip. A LeadGen distributor has acquisition whether or not this
   * flag is set, so the flag is not the whole answer to "is acquisition on".
   *
   * The Access section above changes the package this reads, and its mutation
   * refetches `GetSupplierDetailsData`, so this recomputes from the new tier
   * without a reload.
   */
  const canEditCustomerAcquisition =
    supplier?.accessPackage === ACCESS_PACKAGE.PREMIUM;

  const {
    watch,
    setValue,
    control,
    formState: { errors, defaultValues },
  } = useFormContext<SupplierFormData>();

  const integrationAccess = useIntegrationSettingsAccess();
  const { canChange: canChangeIntegrationSettings } = integrationAccess;
  const isTestDx = isTestDistributor({
    isPuppy: supplier?.isPuppy,
    isTest: supplier?.isTest,
  });

  /**
   * Whether un-integrating this distributor is off limits.
   *
   * The same rule the legacy admin applied to this toggle
   * (`IntegrationSettingsSection.tsx:61`): once a distributor is integrated,
   * only someone who may change its integration settings may turn it off.
   * Without this the guard is trivially bypassed. Turn Integrated off, and all
   * four settings unlock, because they are only locked while it is on.
   */
  const lockIntegratedToggle = isIntegrationPayloadLocked(
    integrationAccess,
    Boolean(watch('isIntegrated')),
    {
      isPuppy: supplier?.isPuppy,
      isTest: supplier?.isTest,
    }
  );
  // Two different questions, so two different values.
  //
  // The pill answers "what does this field say right now", so it follows the
  // field and flips as soon as someone types.
  //
  // The warning answers "is the one-way door still open", which is about what
  // is stored, not what is typed. `defaultValues` is the saved record: the form
  // resets to the response after every successful save.
  const fieldUsesCurrencyDefault = watch('calculationPrecision') === undefined;
  const savedPrecision = defaultValues?.calculationPrecision;
  const savedUsesCurrencyDefault =
    savedPrecision === undefined || savedPrecision === null;

  // Same reasoning for the language warning: it is about the change, so it
  // compares the field against the saved value and disappears again if the
  // reader puts it back. A permanent banner beside the picker would be read
  // once and then never again, which is no warning at all. Nothing is shown
  // while creating: a distributor with no products yet has nothing to lose.
  const isChangingLocale =
    mode === 'edit' && watch('locale') !== defaultValues?.locale;

  return (
    // No Card here: the tab already sits inside the form's card, and a second
    // border around the same content is the nested-panel look this tab had.
    // One divided column, so the dividers carry the rhythm and the sections do
    // not stack their own margins on top of it.
    //
    // Every section is capped at the Profile tab's `max-w-4xl`, so the two read as
    // one form. Capped rather than full width: at full width a right-aligned
    // switch sits a screen away from the label it belongs to.
    <div className="divide-y divide-divider">
      {/* Above Integration and in its own section, because it is the only thing
          on this tab that writes without Save. The whole section is gated, not
          just its contents: gating only the inner control leaves a heading with
          nothing under it, which reads as a group that lost its contents. */}
      {canViewAccessPackage && (
        <section className="max-w-4xl space-y-5 p-6">
          <SectionHeading
            title="Access"
            description="What this distributor is entitled to use."
          />
          <AccessPackageSection
            supplierId={supplier?.id}
            accessPackage={supplier?.accessPackage}
          />
        </section>
      )}

      <section className="max-w-4xl space-y-5 p-6">
        <SectionHeading title="Integration" />
        <LockedFieldTooltip
          isLocked={lockIntegratedToggle}
          canChange={canChangeIntegrationSettings}
        >
          <ToggleRow
            label="Integrated"
            description="This distributor is kept in step with an external system."
            isSelected={watch('isIntegrated')}
            onChange={(value) =>
              setValue('isIntegrated', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            testId="supplier-is-integrated"
            isDisabled={lockIntegratedToggle}
          />
        </LockedFieldTooltip>

        {/* Two conditions, and both matter.
            Integrated, because only then do these settings belong to the
            integration; before that they are ordinary settings, edited on their
            own tabs like everything else.
            And the guard switched on, because with it off the other tabs let
            anyone edit all four. Offering a disabled button and a way to request
            access to settings that are already editable elsewhere is a
            contradiction. Off means the portal behaves as it did before the
            guard existed, here as well. */}
        {integrationAccess.isGuardEnabled &&
          watch('isIntegrated') &&
          !isTestDx && (
            <IntegrationSettingsSection
              canChange={canChangeIntegrationSettings}
            />
          )}
      </section>

      <section className="max-w-4xl space-y-5 p-6">
        <SectionHeading
          title="Checkout"
          description="These decide whether an order is sent through this distributor's promotion and tax setup. Both are off unless you turn them on, and turning one on changes nothing until that setup exists."
        />
        <ToggleRow
          label="Promotions"
          description="Apply this distributor's promotions to buyer orders."
          isSelected={watch('enablePromotions')}
          onChange={(value) =>
            setValue('enablePromotions', value, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          testId="supplier-enable-promotions"
        />
        <ToggleRow
          label="Taxes"
          description="Add tax to buyer orders from this distributor."
          isSelected={watch('enableTaxes')}
          onChange={(value) =>
            setValue('enableTaxes', value, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          testId="supplier-enable-taxes"
        />

        <ChocoSelect
          label="Price on"
          // `ChocoSelect` draws its label as plain text next to the field
          // rather than wiring it to the control, so without this the picker
          // announces its value and never says what the value is for.
          aria-label="Price on"
          description="Which date the prices, promotions and tax on an order are worked out from."
          options={[
            { value: 'ORDER_DATE', label: 'The day the order is placed' },
            { value: 'DELIVERY_DATE', label: 'The day the order is delivered' },
          ]}
          value={watch('pricingDateSource')}
          // The backend has no third value, and an empty pricing date is not a
          // state this form can save, so the picker never offers one.
          disallowEmptySelection
          onSelectionChange={(value) => {
            if (value === 'ORDER_DATE' || value === 'DELIVERY_DATE') {
              setValue('pricingDateSource', value, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }
          }}
          data-testid="supplier-pricing-date-source"
        />

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span
              id="supplier-calculation-precision-label"
              className={ROW_LABEL_CLASS}
            >
              Decimal places
            </span>
            {/* An empty box and a box holding 2 mean different things, and a
                distributor on the euro default also computes to 2 places. The
                pill says which of the two the reader is looking at, so nobody
                has to infer it from an empty field. */}
            <Chip
              size="sm"
              variant="flat"
              color={fieldUsesCurrencyDefault ? 'default' : 'primary'}
              data-testid="supplier-calculation-precision-state"
            >
              {fieldUsesCurrencyDefault ? 'Currency default' : 'Custom'}
            </Chip>
            {/* The one-way door is the whole reason this warning exists: a value
                cannot be taken back out once it is saved, so CS needs to know
                before they pick, not after. Once a number is stored the door has
                already closed, and the line below states that instead. */}
            {savedUsesCurrencyDefault && (
              <Tooltip
                content="Once you save a number here you cannot go back to the currency default. You can still change it to a different number."
                placement="right"
                className="max-w-xs"
              >
                {/* A button, not a span: a span with only an aria-label is left
                    out of the accessibility tree entirely, and it cannot be
                    focused, so the warning would be unreachable without a mouse.
                    It warns about something that cannot be undone. */}
                <button
                  type="button"
                  className="inline-flex cursor-help text-warning focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                  data-testid="supplier-calculation-precision-warning"
                  aria-label="Once you save a number here you cannot go back to the currency default. You can still change it to a different number."
                >
                  <FiAlertTriangle size={14} aria-hidden="true" />
                </button>
              </Tooltip>
            )}
          </div>
          {/* Both lines describe what is saved, not what is typed, so neither
              goes stale the moment someone starts editing. The pill above
              carries the typed state. */}
          <p className={ROW_DESCRIPTION_CLASS}>
            {savedUsesCurrencyDefault
              ? "How many decimal places prices and tax are worked out to. This distributor uses the currency's own, which is 2 for euro, pound and dollar, until a number is saved here."
              : `How many decimal places prices and tax are worked out to. This distributor is set to ${savedPrecision}. It can be changed to another number, but not back to the currency default.`}
          </p>
          <Input
            type="number"
            min={0}
            // The backend accepts 0 to 10. The field does not narrow that: a
            // limit the contract does not have is a limit CS cannot get past.
            max={10}
            variant="bordered"
            classNames={INPUT_CLASS_NAMES}
            aria-labelledby="supplier-calculation-precision-label"
            value={
              watch('calculationPrecision') === undefined
                ? ''
                : String(watch('calculationPrecision'))
            }
            onValueChange={(value) =>
              setValue(
                'calculationPrecision',
                value === '' ? undefined : Number(value),
                { shouldDirty: true, shouldValidate: true }
              )
            }
            placeholder="Currency default"
            errorMessage={errors.calculationPrecision?.message}
            isInvalid={!!errors.calculationPrecision}
            data-testid="supplier-calculation-precision"
          />
        </div>
      </section>

      <section className="max-w-4xl space-y-5 p-6">
        <SectionHeading
          title="Ordering and eCommerce"
          description="What buyers can do on an order, and what the eCommerce storefront shows."
        />

        <ToggleRow
          label="Allow order guides from invoice photos"
          description="Buyers can build an order guide by photographing an invoice."
          isSelected={watch('allowInvoiceOrderGuides')}
          onChange={(value) =>
            setValue('allowInvoiceOrderGuides', value, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          testId="supplier-allow-invoice-order-guides"
        />

        <ToggleRow
          label="Disable customer number edits"
          description="The customer number on an order is locked once it is set."
          isSelected={watch('disableCustomerNumberEdits')}
          onChange={(value) =>
            setValue('disableCustomerNumberEdits', value, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          testId="supplier-disable-customer-number-edits"
        />

        <ToggleRow
          label="Disable ordered product merging"
          // Two lines of the same product stay separate. The legacy admin carried
          // the second sentence and it is the part that surprises people.
          description="Two lines of the same product stay separate. This also stops orders being edited."
          isSelected={watch('disableOrderedProductMerging')}
          onChange={(value) =>
            setValue('disableOrderedProductMerging', value, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          testId="supplier-disable-ordered-product-merging"
        />

        <ToggleRow
          label="Product grid in eCommerce"
          description="The eCommerce catalog shows products as a grid instead of a list."
          isSelected={watch('enableEcommerceProductGrid')}
          onChange={(value) =>
            setValue('enableEcommerceProductGrid', value, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          testId="supplier-ecommerce-product-grid"
        />

        <ToggleRow
          label="PO number in eCommerce"
          description="eCommerce checkout asks the buyer for a purchase order number."
          isSelected={watch('allowEcommercePoNumber')}
          onChange={(value) =>
            setValue('allowEcommercePoNumber', value, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          testId="supplier-ecommerce-po-number"
        />

        {/* Hidden while creating, as it was in the legacy admin: the access
            package is not set yet, so the rule that governs this row has nothing
            to read. */}
        {mode === 'edit' && (
          <ToggleRow
            label="Allow customer acquisition"
            description="Buyers can enquire about new business with this distributor."
            isSelected={watch('allowCustomerAcquisition')}
            onChange={(value) =>
              setValue('allowCustomerAcquisition', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            lockedReason={
              canEditCustomerAcquisition
                ? undefined
                : 'Only a Premium distributor can have this changed.'
            }
            testId="supplier-allow-customer-acquisition"
          />
        )}
      </section>

      {/* No section heading: the pickers already label themselves, and two
          "Language" headings in a row was the old tab's worst tell. Time zone
          sits with the language because both are distributor identity, and this
          is where CS looks for that. */}
      <section className="max-w-4xl space-y-5 p-6">
        <Controller
          name="locale"
          control={control}
          render={({ field }) => (
            <LocaleSelectField
              value={field.value}
              onChange={field.onChange}
              isRequired
              errorMessage={errors.locale?.message}
              data-testid="supplier-locale-field"
              helperText="Sets the distributor's default currency and date formats."
            />
          )}
        />
        {isChangingLocale && (
          <div data-testid="supplier-locale-change-warning">
            <AlertCard
              type="warning"
              title="Changing the language can break the catalog"
              message="If this distributor already has unavailable products, changing the default locale will cause issues with product visibility. Remove all products from the catalog and re-import them to fix, or contact @choco-integrations-chef in slack to fix the products manually without removal."
            />
          </div>
        )}
        <TimezoneField />
      </section>
      {/* No Reference ID row. It is read-only and never submitted, so it does
          not belong in a form, and the page header now shows it as a copy
          control, which is what CS actually does with it. */}
    </div>
  );
};
